/**
 * A `Company change` row — the same person at a new employer.
 *
 * A ROW, not a card, and that is the point. These land in the conflict
 * machinery only because the identity rule keys on name + company, so an
 * employer move breaks the key exactly as a different person on the same URL
 * does. In a recruiting database the move is the NORMAL case — measured on the
 * shipped compare fixture, every W2 row is one — so giving each a full
 * side-by-side card with four actions buried the real conflicts under a block
 * containing no actual conflict.
 *
 * A title change at the same employer never reaches here at all (it flows
 * through as `linkedin_position_update`), which is why the section is named for
 * the employer rather than for the job.
 *
 * PRE-CONFIRMED. The checkbox is on by default, and the modal materializes that
 * into a real `update_anyway` decision on Continue — a checked box the
 * recruiter did not tick is a promise, and the only way to keep it is to send
 * what it shows. Unchecking leaves the Thrive contact alone.
 *
 * THE TICK SURVIVES fu5 SUB-TASK 02's "no defaults" SWEEP — a deliberate
 * exception, not an oversight. That sub-task removed the neutral default from
 * the CONFLICT cards, where the default made "nobody looked" and "somebody
 * looked and left it" indistinguishable. These rows are not conflicts: they are
 * a job move, reaching the conflict machinery only because the identity rule
 * keys on name + company, and Jared reviewed the section live on 2026-09-08
 * without objecting to the tick (he asked only for a rename). The row is
 * therefore never counted as UNDECIDED and never blocks Continue. Do not "fix"
 * this for consistency with the cards one section down.
 */

import type { ConflictContact } from "@nu-assist/contracts";
import { Avatar } from "@nu-assist/ui";
import { thriveContactUrl } from "../../lib/thriveLinks";
import { CONFLICT_ACTION_LABELS, LEAVE_OUT_HELPER, companyTransition } from "./conflictActionsCopy";

export interface CompanyChangeRowProps {
  contact: ConflictContact;
  /** True when the row will update Thrive on Continue. */
  confirmed: boolean;
  onConfirmedChange: (confirmed: boolean) => void;
  /** Presigned S3 GET URL for the source row's stored avatar. */
  resolvedAvatarUrl?: string | null;
  /** Persisted, undoable dismissal — the SAME `leaveOut` the conflict cards
   *  call, not a second mechanism.
   *
   *  Until 2026-09-08 this section had no way to reach it, and the hole was
   *  invisible from inside the component: `W2ConflictCard` offers four actions
   *  and this row offered one, so a contact filed as a company change could
   *  only ever be confirmed. A recruiter had no way to say "same person, new
   *  employer, and still not for this search". The E2E suite found it by timing
   *  out for 900 s clicking a button that does not exist here. */
  onIgnore: () => void;
}

function fullName(first?: string | null, last?: string | null): string {
  return [first, last].filter(Boolean).join(" ").trim();
}

export function CompanyChangeRow({
  contact,
  confirmed,
  onConfirmedChange,
  resolvedAvatarUrl,
  onIgnore,
}: CompanyChangeRowProps) {
  const id = contact.import_contact_id;
  // Both sides carry the SAME name — that is what put the row in this section —
  // so it is printed once rather than twice across a comparison the recruiter
  // does not have to make.
  const name = fullName(contact.first_name, contact.last_name) || "This row";

  return (
    <li
      data-testid="company-change-row"
      id={`conflict-card-${id}`}
      className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2"
    >
      {/* The whole identity block is the checkbox's hit area (operator,
          2026-09-08), not the 16px box alone — a tick that small is a poor
          target for a row the recruiter is meant to sweep through quickly.
          `<label>` rather than an onClick on the `<li>`: it is what makes the
          association real for a screen reader and for keyboard users, instead
          of a mouse-only affordance bolted on.

          IT STOPS BEFORE THE THRIVE COLUMN, deliberately. Two interactive
          things live to the right — the `#id →` link and the Skip button —
          and a label spanning the full row would swallow the gaps between
          them, so a stray click would silently toggle a Thrive write. Over the
          link itself the browser would fire BOTH.

          THAT RULE IS NOW A PRIMITIVE. `HighlightOptionGroup`'s `trailing` slot
          renders outside the activating element for exactly this reason, and
          the conflict cards one section down inherit the behaviour rather than
          re-deriving it. This comment is where it was learned.

          `aria-label` is kept even though the label now has text: that text
          says WHO ("Lee Park, Acme Corp → Correct Company") and never what
          ticking it does. The explicit label is the only thing stating the
          action. */}
      <label
        data-testid={`company-change-confirm-label-${id}`}
        className="-mx-1 flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-sm px-1 py-0.5 hover:bg-bg"
      >
        <input
          type="checkbox"
          data-testid={`company-change-confirm-${id}`}
          checked={confirmed}
          onChange={(event) => onConfirmedChange(event.target.checked)}
          aria-label={`Update ${name} in Thrive`}
          className="size-4 shrink-0 accent-accent"
        />
        <Avatar
          src={contact.profile_image_s3_key ? resolvedAvatarUrl : undefined}
          name={name}
          size="sm"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-body text-text">{name}</p>
          <p className="truncate text-caption text-text-secondary">
            {companyTransition(contact.thrive_company, contact.company)}
          </p>
        </div>
      </label>
      {/* THRIVE'S OWN face, beside the import row's. The whole section asks the
          recruiter for a fast visual "yes, same person" before they leave a row
          ticked, and one photo cannot answer that — the pair can, at a glance,
          without opening anything.

          `name` is deliberately NOT passed: initials would be drawn from the
          IMPORT row's name, and printing those under Thrive's id would assert
          the two are the same person, which is the very thing the recruiter is
          being asked to confirm. The neutral silhouette asserts nothing. Same
          rule the contacts table's Thrive-avatar cell follows. */}
      <div
        data-testid={`company-change-thrive-avatar-${id}`}
        className="flex shrink-0 items-center gap-2"
      >
        <Avatar src={contact.thrive_avatar_url} size="sm" />
        {contact.contact_id_thrive && (
          <a
            data-testid={`company-change-thrive-link-${id}`}
            href={thriveContactUrl(contact.contact_id_thrive)}
            target="_blank"
            rel="noreferrer"
            className="text-caption text-accent hover:underline"
          >
            #{contact.contact_id_thrive} →
          </a>
        )}
      </div>

      {/* The row's third zone, and the only destructive-ish one. Last, and
          outside the label, so the three intentions read left to right in
          order of commitment: confirm the update · go look in Thrive · drop it
          from this export.

          Ticking and skipping cannot both apply, and SKIPPING WINS by
          construction rather than by anything this component does: the modal
          derives `companyChanges` from `w2Active`, which filters out left-out
          rows, so a skipped contact leaves this section (and lands in the
          Skipped tab) on the same render. Unticking here first would be
          theatre — the row is about to stop existing. */}
      {/* Byte-identical to `W2ConflictCard`'s Undo button (`shrink-0` aside,
          which this row needs and a centred card footer does not). The first
          cut used `text-text-secondary`, which made it read as a lesser
          control than the one doing the same class of thing one section down
          — and "the layouts should be analogous" is the whole point of the
          section this button lives in being a section at all. */}
      <button
        type="button"
        data-testid={`company-change-ignore-${id}`}
        onClick={onIgnore}
        title={LEAVE_OUT_HELPER}
        className="shrink-0 rounded-md border border-border bg-bg px-2 py-1 text-label text-text hover:bg-surface"
      >
        {CONFLICT_ACTION_LABELS.removeFromList}
      </button>
    </li>
  );
}
